import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TenantAPI, extractError } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { BranchesPanel } from "@/pages/_BranchesPanel";

export default function SettingsPage() {
    const { t } = useT();
    const { tenant, refresh, permissions } = useAuth();
    const canEditTenant = permissions.includes("tenant:update");
    const canEditSettings = permissions.includes("settings:update");

    return (
        <div className="space-y-6 animate-fade-in" data-testid="settings-page">
            <header>
                <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                    {t("nav.settings")}
                </h1>
                <p className="mt-1 text-sm text-zinc-500">
                    Workspace, branches, security — all scoped to {tenant?.name}.
                </p>
            </header>

            <Tabs defaultValue="tenant" className="space-y-4">
                <TabsList data-testid="settings-tabs">
                    <TabsTrigger value="tenant" data-testid="tab-tenant">
                        {t("settings.tab_tenant")}
                    </TabsTrigger>
                    <TabsTrigger value="branches" data-testid="tab-branches">
                        {t("settings.tab_branches")}
                    </TabsTrigger>
                    <TabsTrigger value="security" data-testid="tab-security">
                        {t("settings.tab_security")}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="tenant" className="space-y-4">
                    <TenantPanel canEditTenant={canEditTenant} onSaved={refresh} />
                    <SettingsPanel canEdit={canEditSettings} />
                </TabsContent>

                <TabsContent value="branches">
                    <BranchesPanel />
                </TabsContent>

                <TabsContent value="security">
                    <Card className="p-6 text-sm text-zinc-500">
                        Password rotation, MFA and session management are coming soon. For now, accounts use
                        bcrypt-hashed passwords with 5-attempt soft lockout and per-IP rate limiting.
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function TenantPanel({ canEditTenant, onSaved }) {
    const { tenant } = useAuth();
    const [name, setName] = useState(tenant?.name || "");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        setName(tenant?.name || "");
    }, [tenant]);

    async function save() {
        setBusy(true);
        try {
            await TenantAPI.update({ name });
            toast.success("Tenant updated");
            onSaved && onSaved();
        } catch (e) {
            toast.error(extractError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
                <h2 className="font-display text-lg font-bold tracking-tight">Workshop identity</h2>
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                    /{tenant?.slug}
                </span>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                        Workshop name
                    </Label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={!canEditTenant}
                        data-testid="tenant-name-input"
                    />
                </div>
            </div>
            <div className="mt-4 flex justify-end">
                <Button
                    onClick={save}
                    disabled={!canEditTenant || busy}
                    data-testid="tenant-save"
                    className="bg-brand-red text-white hover:bg-brand-redDeep"
                >
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save
                </Button>
            </div>
        </Card>
    );
}

function SettingsPanel({ canEdit }) {
    const [settings, setSettings] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        TenantAPI.getSettings().then(setSettings).catch((e) => toast.error(extractError(e)));
    }, []);

    function set(k, v) {
        setSettings((s) => ({ ...s, [k]: v }));
    }

    async function save() {
        setBusy(true);
        try {
            const upd = await TenantAPI.updateSettings({
                default_currency: settings.default_currency,
                default_locale: settings.default_locale,
                tax_mode: settings.tax_mode,
                labour_rate_per_hour_minor: Number(settings.labour_rate_per_hour_minor),
                invoice_prefix_template: settings.invoice_prefix_template,
                fy_start_month: Number(settings.fy_start_month),
            });
            setSettings(upd);
            toast.success("Settings saved");
        } catch (e) {
            toast.error(extractError(e));
        } finally {
            setBusy(false);
        }
    }

    if (!settings) return <Card className="p-6 text-sm text-zinc-500">Loading settings…</Card>;

    return (
        <Card className="p-6" data-testid="settings-panel">
            <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Operating defaults</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Default currency (ISO-4217)">
                    <Input
                        value={settings.default_currency || ""}
                        className="font-mono uppercase"
                        onChange={(e) => set("default_currency", e.target.value.toUpperCase())}
                        disabled={!canEdit}
                        data-testid="set-currency"
                    />
                </Field>
                <Field label="Default locale">
                    <Input
                        value={settings.default_locale || ""}
                        onChange={(e) => set("default_locale", e.target.value)}
                        disabled={!canEdit}
                        data-testid="set-locale"
                    />
                </Field>
                <Field label="Tax mode">
                    <select
                        value={settings.tax_mode || "none"}
                        onChange={(e) => set("tax_mode", e.target.value)}
                        disabled={!canEdit}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                        data-testid="set-tax"
                    >
                        <option value="gst">GST (India)</option>
                        <option value="vat">VAT</option>
                        <option value="none">None</option>
                    </select>
                </Field>
                <Field label="Labour rate / hour (minor units)">
                    <Input
                        type="number"
                        value={settings.labour_rate_per_hour_minor ?? 0}
                        onChange={(e) => set("labour_rate_per_hour_minor", e.target.value)}
                        disabled={!canEdit}
                        className="font-mono tnum"
                        data-testid="set-rate"
                    />
                </Field>
                <Field label="Invoice prefix template">
                    <Input
                        value={settings.invoice_prefix_template || ""}
                        onChange={(e) => set("invoice_prefix_template", e.target.value)}
                        disabled={!canEdit}
                        className="font-mono"
                        data-testid="set-prefix"
                    />
                </Field>
                <Field label="Fiscal year start month (1-12)">
                    <Input
                        type="number"
                        min={1}
                        max={12}
                        value={settings.fy_start_month ?? 4}
                        onChange={(e) => set("fy_start_month", e.target.value)}
                        disabled={!canEdit}
                        className="font-mono tnum"
                        data-testid="set-fy"
                    />
                </Field>
            </div>
            <div className="mt-4 flex justify-end">
                <Button
                    onClick={save}
                    disabled={!canEdit || busy}
                    data-testid="settings-save"
                    className="bg-brand-red text-white hover:bg-brand-redDeep"
                >
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save
                </Button>
            </div>
        </Card>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                {label}
            </Label>
            {children}
        </div>
    );
}
